/**
 * Collects device / browser context metadata.
 * All values are derived from standard browser APIs — no external library needed.
 * Sent to the backend at liveness/create so they appear in the webhook
 * request_context block.
 */
export interface DeviceMetadata {
  device_type: "mobile" | "desktop" | "tablet";
  os_name: string;
  browser_timezone: string;
  fingerprint_id: string;
}

function detectOs(ua: string): string {
  if (/Windows NT/i.test(ua)) return "Windows";
  if (/Mac OS X|Macintosh/i.test(ua)) return "macOS";
  if (/Android/i.test(ua)) return "Android";
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Linux/i.test(ua)) return "Linux";
  if (/CrOS/i.test(ua)) return "ChromeOS";
  return "Unknown";
}

function getOrCreateFingerprintId(): string {
  const KEY = "_adhere_fid";
  try {
    let fid = localStorage.getItem(KEY);
    if (!fid) {
      fid =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(KEY, fid);
    }
    return fid;
  } catch {
    // localStorage unavailable (e.g. private browsing with strict settings)
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
}

export function collectDeviceMetadata(): DeviceMetadata {
  const ua = navigator.userAgent;

  const isTablet =
    /iPad/i.test(ua) ||
    (/Android/i.test(ua) && !/Mobile/i.test(ua));

  const isMobile =
    !isTablet &&
    /Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);

  const device_type: DeviceMetadata["device_type"] = isTablet
    ? "tablet"
    : isMobile
    ? "mobile"
    : "desktop";

  return {
    device_type,
    os_name: detectOs(ua),
    browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    fingerprint_id: getOrCreateFingerprintId(),
  };
}
